import React, { useMemo, useRef } from 'react';

import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import {
  BookingInquiryDialogs,
  BookingInquiryDialogsRef,
} from '@components/admin/booking/BookingInquiryDialogs';
import { InfoManagement } from '@components/admin/booking/InfoManagement';
import { TicketBubble } from '@components/admin/booking/TicketBubble';
import { FormTitle, DetailTextDisplay } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { BookingInquiryDisplay } from '@components/utilities/BookingInquiry/BookingInquiryDisplay';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useZendeskD2dTickets } from '@hooks/queries/dashboard/useZendeskD2dTickets';
import { useUser } from '@hooks/queries/useUser';
import { useSearch } from '@hooks/useSearch';
import Collapse from '@material-ui/core/Collapse';
import { BookingInquiryFormFields } from '@models/BookingInquiry';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { getBreadcrumb, url } from '@utils/pages';
import { toJST } from '@utils/types';
import { useLocalStorageState } from '@utils/useLocalStorageState';
import clsx from 'clsx';
import * as t from 'io-ts';
import * as h from 'tyrann-io';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: '詳細',
    managementIntelligence: '管理情報',
    customerIntelligence: '顧客情報',
    bookingInquiry: '予約内容',
    companyName: '会社名',
    memberNo: '会員番号',
    author: '作成者',
    mobileNum: '携帯電話番号',
    phoneNum: '固定電話番号',
    email: 'メールアドレス',
    d2dNo: 'D2D番号',
    paymentTime: '受付時間',
    personInChargeOfBusiness: '業務担当者',
    status: '対応状況',
    status2: 'ステータス',
    becomeAssignee: '担当者になる',
    change: '変更',
    createNewPreAlert: 'PRE ALERTを新規作成',
    dateTime: '日時',
    remark: '備考',
    document: '書類',
    addTransportStatus: '輸送状況を追加する',
    notEditableHint: '担当者のみが編集可能です。',
    changeLog: '変更履歴',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    flex: 1,
    padding: '1.5rem 2rem',
    overflowY: 'hidden',
  },
  leftColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    flex: 2.25,
    overflowY: 'scroll',
  },
  rightColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    width: 360,
    overflowY: 'scroll',
  },
  textDisplayContainer: {
    ...common.flexInline,
    ...common.middleText,
    alignItems: 'flex-start',
    padding: 5,
    color: theme.palette.grey[700],
  },
  textDisplayYContainer: {
    ...common.flexColumn,
    ...common.middleText,
    alignItems: 'flex-start',
    padding: 5,
    color: theme.palette.grey[700],
  },
  textDisplayBottomItem: {
    ...common.flexInline,
    width: 240,
    '& a': {
      fontWeight: 'bold',
      alignItems: 'center',
      textDecorationLine: 'underline',
      color: theme.palette.primary.main,
    },
    '&$textDisplayItemValue': {
      flex: 1,
      whiteSpace: 'pre-wrap',
    },
    padding: 20,
  },
  textDisplayYItem: {
    fontWeight: 500,
  },
  innerTabPanel: {
    padding: '1rem',
  },
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
}));

const searchType = t.type({
  d2dNo: h.string(),
});

export const InquiryDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);

  const bookingInquiryDialogsRef = useRef<BookingInquiryDialogsRef>(null);

  const breadcrumb = useMemo(
    () => [...getBreadcrumb(url('/admin/booking/BookingInquiries')), String(search.d2dNo)],
    [],
  );

  const user = useUser();
  const preAlertItem = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: search.d2dNo,
    },
  });
  const inquiryDetail = useBookingInquiryDetailByD2dNo(search.d2dNo);

  const zendeskTickets = useZendeskD2dTickets(search.d2dNo);

  const ownerTicket = useMemo(() => {
    return zendeskTickets.data?.find(
      (item) => item.requester.email === inquiryDetail.data?.owner?.email,
    );
  }, [zendeskTickets.data, inquiryDetail.data]);

  const [customerIntelligenceExpanded, setCustomerIntelligenceExpanded] = useLocalStorageState(
    'INQUIRY_DETAILS_CUSTOMER_INTELLIGENCE_EXPANDED',
    z.boolean(),
    true,
  );
  const [inquiryExpanded, setInquiryExpanded] = useLocalStorageState(
    'INQUIRY_DETAILS_INQUIRY_EXPANDED',
    z.boolean(),
    true,
  );

  return (
    <Layout
      title={strings.title}
      loading={inquiryDetail.isLoading || preAlertItem.isLoading || user.isLoading}
      errors={[inquiryDetail.error, preAlertItem.error, user.error]}
      breadcrumb={breadcrumb}
    >
      {() => (
        <div className={classes.container}>
          <div className={classes.leftColumn}>
            <FormTitle
              title={strings.customerIntelligence}
              noEdit
              noExpand={false}
              expanded={customerIntelligenceExpanded}
              onChangeExpanded={setCustomerIntelligenceExpanded}
            />
            <div className={classes.formContainer}>
              <Collapse in={customerIntelligenceExpanded}>
                <DetailTextDisplay
                  field={BookingInquiryFormFields.shared.companyName}
                  value={
                    inquiryDetail.data?.owner_company?.company_name ? (
                      <a
                        className={classes.link}
                        href={`${url('/admin/members/MemberDetails')}?id=${
                          inquiryDetail.data?.owner_company?.id
                        }`}
                        target="_blank"
                      >
                        {inquiryDetail.data?.owner_company?.company_name}
                      </a>
                    ) : (
                      '-'
                    )
                  }
                />
                <DetailTextDisplay
                  label={'Owner'}
                  value={
                    <>
                      {`${inquiryDetail.data?.owner_company?.company_name ?? '-'} (${
                        inquiryDetail.data?.owner
                          ? new UserHelper(inquiryDetail.data?.owner).fullname
                          : '-'
                      })`}
                      {inquiryDetail.data?.owner && zendeskTickets.isSuccess && (
                        <TicketBubble
                          d2dNo={inquiryDetail.data.d2d_no}
                          userId={inquiryDetail.data?.owner.id}
                          ticket={ownerTicket}
                        />
                      )}
                    </>
                  }
                />
                <DetailTextDisplay
                  field={BookingInquiryFormFields.shared.memberNo}
                  value={inquiryDetail.data?.owner_company?.member_no ?? '-'}
                />
                <DetailTextDisplay
                  field={BookingInquiryFormFields.shared.mobileNum}
                  value={inquiryDetail.data?.owner?.cellphone}
                />
                <DetailTextDisplay
                  field={BookingInquiryFormFields.shared.phoneNum}
                  value={inquiryDetail.data?.owner?.landline}
                />
                <DetailTextDisplay
                  field={BookingInquiryFormFields.shared.email}
                  value={inquiryDetail.data?.owner?.email}
                />
                <DetailTextDisplay
                  field={BookingInquiryFormFields.shared.author}
                  value={`${inquiryDetail.data?.user.last_name} ${inquiryDetail.data?.user.first_name}`}
                />
              </Collapse>
            </div>
            <FormTitle
              title={strings.bookingInquiry}
              noEdit
              noExpand={false}
              expanded={inquiryExpanded}
              onChangeExpanded={setInquiryExpanded}
            />

            <Collapse in={inquiryExpanded}>
              <div className={classes.formContainer}>
                {inquiryDetail.data && preAlertItem.data && (
                  <>
                    <BookingInquiryDisplay
                      bookingInquiry={inquiryDetail.data}
                      variant="content"
                      type="admin"
                      editDisabled={
                        user.data?.id !== inquiryDetail.data?.salesperson_china?.id &&
                        user.data?.id !== inquiryDetail.data?.salesperson_japan?.id
                      }
                      editDisabledReason={strings.notEditableHint}
                      onOpenForm={(name) => bookingInquiryDialogsRef.current?.onOpenForm(name)}
                    />
                    <BookingInquiryDialogs
                      ref={bookingInquiryDialogsRef}
                      adminInquiryDetail={inquiryDetail.data}
                      preAlertDetails={preAlertItem.data}
                    />
                  </>
                )}

                <div style={{ height: 16 }} />

                {inquiryDetail.data && (
                  <div
                    className={clsx(
                      classes.textGray800,
                      classes.middleText,
                      classes.flexInline,
                      classes.justifyBetween,
                    )}
                  >
                    <span>Latest Update: {toJST(new Date(inquiryDetail.data.updated_at!))}</span>

                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`${url('/utilities/Bookings/BookingInquiryLogs')}?d2d_no=${
                        inquiryDetail.data.d2d_no
                      }&admin=${true}`}
                      className={clsx(classes.link, classes.baseButton)}
                    >
                      {strings.changeLog}
                    </a>
                  </div>
                )}
              </div>
            </Collapse>
          </div>
          <div className={classes.rightColumn}>
            <InfoManagement bookingInquiry={inquiryDetail.data!} />
          </div>
        </div>
      )}
    </Layout>
  );
};
